import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { PersonalInformationContainer, Banner, FormContainer, Buttons } from './simaf.styles';
import { RootState } from 'src/store/store';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import ModalAllDataRequired from 'src/components/shared/modals/allDataRequired/allDataRequired';
import ModalErrorUploadingInput from 'src/components/shared/modals/errorUploadingInput/errorUploadingInput';
import { FormValues } from './simaf.types';
import { InputField } from 'src/utilities/UILibrary/Forms/InputField/InputField';
import { Button } from 'src/utilities/UILibrary/UI/Button/Button';
import Card from 'src/utilities/UILibrary/UI/Card/Card';
import options from 'src/constants/simafOptionsInvestor';
import { environment, url } from 'src/environments';
import { InputTextArea } from 'src/utilities/UILibrary/Forms/InputTextArea/InputTextArea';
import { useManageSteps } from 'src/hooks/useManageSteps';
import { setLoadingMessage, setLoadingState } from 'src/store/User/user.slice';

export default function Simaf() {
  const [allDataRequired, setAllDataRequired] = useState(false);
  const [errorUploadingInput, setErrorUploadingInput] = useState(false);
  const user = useSelector((state: RootState) => state.user.currentUser);
  const { control, handleSubmit } = useForm<FormValues>();
  const { nextStep } = useManageSteps();
  const dispatch = useDispatch();

  const [monthlyAmount, monthlyOperations, resourcesOrigin, resourcesDestination, typeOfClient] =
    options;

  const monthlyOperationsOptions = monthlyOperations.map((operation) => ({
    value: operation,
    label: operation
  }));
  const monthlyAmountOptions = monthlyAmount.map((amount) => ({
    value: amount,
    label: amount
  }));
  const resourcesOriginOptions = resourcesOrigin.map((origin) => ({
    value: origin,
    label: origin
  }));
  const resourcesDestinationOptions = resourcesDestination.map((destination) => ({
    value: destination,
    label: destination
  }));
  const typeOfClientOptions = typeOfClient.map((client) => ({
    value: client,
    label: client
  }));

  const navigate = useNavigate();

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    //Ejecuta el endpoint registro-cuenta-persona-fisica de STP
    dispatch(setLoadingMessage(''));
    dispatch(setLoadingState(true));
    //Ejecuta el endpoint registro-cuenta-persona-fisica de STP  registro-cuenta-persona-fisica
    const stpInput = {
      clientId: user.id,
      tipoCliente: 'investor'
    };
    const config = {
      method: 'put',
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_STPURL_FINSPHERA}/registro-cuenta-persona-fisica`,
      headers: {
        apiKey: `${process.env.REACT_APP_APIKEY_STP}`,
        'Content-Type': 'application/json'
      },
      data: stpInput
    };
    try {
      const response = await axios(config);
      const updateInput = {
        id: user.id,
        typeOfUser: 'investor',
        frecuencia: data.monthlyOperations,
        monto: data.monthlyAmount,
        dateTime: Date.now(),
        tipoCuenta: 1, //Inversionista es 1
        tipoFinanciamiento: 1,
        tipoProducto: 'Inversión',
        nivelCuenta: 1, //Resolver de matriz de riesgos de oficial de cumplimiento
        numeroCuentaProyecto: user.id,
        regimen: 0, //Normal (0) o Simlpificado (1)
        origenRecursos: data.resourcesOrigin,
        destinoRecursos: data.resourcesDestination,
        proposito: data.investmentDescription
      };
      const simaf = await axios.post(`${url}/${environment}/simaf/create-table`, updateInput);
      dispatch(setLoadingState(false));
      await nextStep();
      navigate('/inversionista/onboarding-phone');
      return simaf;
    } catch (error) {
      dispatch(setLoadingState(false));
      console.error(error);
      setErrorUploadingInput(true);
    }
  };

  const closeFlow = () => {
    navigate('/inversionista/dashboard');
  };
  return (
    <PersonalInformationContainer>
      <ModalAllDataRequired
        showModalAllDataRequired={allDataRequired}
        setAllDataRequired={setAllDataRequired}
      />
      <ModalErrorUploadingInput
        showModalErrorUploadingInput={errorUploadingInput}
        setErrorUploadingInput={setErrorUploadingInput}
      />
      <Banner>
        <figure>
          <img
            src={`${process.env.REACT_APP_BUCKET_FINSPHERA}/icon_inv_personalInformation2_banner.png`}
            alt="icono informacion personal"
          />
        </figure>
        <h2>Perfil de uso de aplicación</h2>
      </Banner>
      <FormContainer>
        <Card className="card-container" isSlim isForm>
          <Card.Row>
            <Controller
              name="monthlyOperations"
              control={control}
              defaultValue={undefined}
              rules={{ required: 'Es un campo requerido.' }}
              render={({ field, fieldState, ...rest }) => (
                <InputField
                  label="Número de operaciones mensuales:"
                  placeholder="Selecciona una opción"
                  isDropdown
                  options={monthlyOperationsOptions}
                  onChangeInternalValue={field.onChange}
                  errorMessages={
                    fieldState.error && fieldState.error.message ? [fieldState.error.message] : []
                  }
                  {...rest}
                />
              )}
            />
          </Card.Row>
          <Card.Row>
            <Controller
              name="monthlyAmount"
              control={control}
              defaultValue={undefined}
              rules={{ required: 'Es un campo requerido.' }}
              render={({ field, fieldState, ...rest }) => (
                <InputField
                  label="Monto acumulado de operaciones mensuales:"
                  placeholder="Selecciona una opción"
                  isDropdown
                  options={monthlyAmountOptions}
                  onChangeInternalValue={field.onChange}
                  errorMessages={
                    fieldState.error && fieldState.error.message ? [fieldState.error.message] : []
                  }
                  {...rest}
                />
              )}
            />
          </Card.Row>
          <Card.Row>
            <Controller
              name="resourcesOrigin"
              control={control}
              defaultValue={undefined}
              rules={{ required: 'Es un campo requerido.' }}
              render={({ field, fieldState, ...rest }) => (
                <InputField
                  label="Origen de los recursos:"
                  placeholder="Selecciona una opción"
                  isDropdown
                  options={resourcesOriginOptions}
                  onChangeInternalValue={field.onChange}
                  errorMessages={
                    fieldState.error && fieldState.error.message ? [fieldState.error.message] : []
                  }
                  {...rest}
                />
              )}
            />
          </Card.Row>
          <Card.Row>
            <Controller
              name="resourcesDestination"
              control={control}
              defaultValue={undefined}
              rules={{ required: 'Es un campo requerido.' }}
              render={({ field, fieldState, ...rest }) => (
                <InputField
                  label="Destino de los recursos:"
                  placeholder="Selecciona una opción"
                  isDropdown
                  options={resourcesDestinationOptions}
                  onChangeInternalValue={field.onChange}
                  errorMessages={
                    fieldState.error && fieldState.error.message ? [fieldState.error.message] : []
                  }
                  {...rest}
                />
              )}
            />
          </Card.Row>
          <Card.Row>
            <Controller
              name="typeOfClient"
              control={control}
              defaultValue={undefined}
              rules={{ required: 'Es un campo requerido.' }}
              render={({ field, fieldState, ...rest }) => (
                <InputField
                  label="Tipo de usuario:"
                  placeholder="Selecciona una opción"
                  isDropdown
                  options={typeOfClientOptions}
                  onChangeInternalValue={field.onChange}
                  errorMessages={
                    fieldState.error && fieldState.error.message ? [fieldState.error.message] : []
                  }
                  {...rest}
                />
              )}
            />
          </Card.Row>
          <Card.Row>
            <Controller
              name="investmentDescription"
              control={control}
              rules={{
                minLength: {
                  value: 10,
                  message: 'Descripción de mínimo 10 caracteres'
                },
                maxLength: {
                  value: 100,
                  message: 'Descripción de máximo 100 caracteres'
                },
                required: 'Es un campo requerido.'
              }}
              render={({ field: { onChange, ...rest }, fieldState: { error } }) => (
                <InputTextArea
                  label="Propósito de inversión con Finsphera:"
                  placeholder="Breve descripción"
                  maxLength={100}
                  onChange={onChange}
                  errorMessages={error && error.message ? [error.message] : []}
                  {...rest}
                />
              )}
            />
          </Card.Row>
        </Card>
      </FormContainer>
      <Buttons>
        <Button size="small" onClick={closeFlow}>
          Cerrar
        </Button>
        <Button size="small" type="submit" onClick={handleSubmit(onSubmit)}>
          Siguiente
        </Button>
      </Buttons>
    </PersonalInformationContainer>
  );
}
